/** @format */
import { useSpring } from '@react-spring/web';
import { MutableRefObject, useEffect } from 'react';

import { ProductBoxProps } from '../types';

/**
 * Gets the animated style for the product box
 *
 * @format
 * @param boxListRef A ref to product box list
 * @param productBoxProps ProductBoxProps
 * @returns Animated react-spring style properties
 */

export const useBoxListAnimation = (
    boxListRef: MutableRefObject<HTMLElement>,
    {
        isCollapsable,
        isCollapsed,
    }: Pick<ProductBoxProps, 'isCollapsable' | 'isCollapsed'>
) => {
    const getBoxListSpringHeight = () => {
        if (isCollapsable && isCollapsed) {
            return 0;
        }

        return boxListRef?.current?.scrollHeight;
    };

    const [boxListAnimatedContainerStyle, boxListAnimatedContainerAPI] =
        useSpring(() => ({
            height: getBoxListSpringHeight(),
        }));

    const setBoxListSpringHeight = () => {
        try {
            boxListAnimatedContainerAPI.set({
                height: getBoxListSpringHeight(),
            });
        } catch (error) {
            return;
        }
    };

    const animateBoxListSpringHeight = () => {
        try {
            boxListAnimatedContainerAPI.start({
                height: getBoxListSpringHeight(),
            });
        } catch (error) {
            return;
        }
    };

    /**
     * set the box list height spring to it's saved state for collapsing / non-collapsing
     */
    useEffect(() => {
        setBoxListSpringHeight();
    }, [isCollapsable]);

    /**
     * set the box list spring height to the box list height when the inner content height changes
     */
    useEffect(() => {
        if (!isCollapsable) {
            setBoxListSpringHeight();
        }
    }, [boxListRef?.current?.scrollHeight]);

    /**
     * animate to the new height when visibility is toggled
     */
    useEffect(() => {
        animateBoxListSpringHeight();
    }, [isCollapsed]);

    return boxListAnimatedContainerStyle;
};
